import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  Search, 
  Download, 
  MoreHorizontal,
  User,
  Users,
  Shield,
  Mail,
  Calendar,
  Building2,
  CheckCircle,
  XCircle,
  Clock,
  ChevronUp,
  ChevronDown,
  RefreshCw,
  RotateCcw
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ColumnConfigDropdown, ColumnConfig } from '@/components/ColumnConfigDropdown';
import { Pagination } from '@/components/ui/pagination';
import { apiRequest } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';

interface ACCUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  profileImageUrl: string;
  isActive: boolean;
  lastAccessedAt: string;
  createdAt: string;
  updatedAt: string;
  projectIds: string[];
  projectNames: string[];
  companyIds: string[];
  companyNames: string[];
  status: 'active' | 'inactive';
}

interface Project {
  id: string;
  name: string;
}

const AdminACCUsers: React.FC = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  // State declarations first
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [projectFilter, setProjectFilter] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);
  const [sortField, setSortField] = useState<string>('createdAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [isScrolled, setIsScrolled] = useState(false);
  const [expandedProjects, setExpandedProjects] = useState<Set<string>>(new Set());

  // Fetch projects for filter
  const { data: projectsData } = useQuery<{projects: Project[]}>({
    queryKey: ['/api/admin/acc-users/projects'],
    queryFn: async () => {
      const data = await apiRequest('/api/admin/acc-users/projects');
      return data;
    },
  });

  // Get user data from ACC Users API
  const { data: users = [], isLoading, error, refetch } = useQuery<ACCUser[]>({
    queryKey: ['/api/admin/acc-users', projectFilter],
    queryFn: async () => {
      const url = projectFilter === 'all' 
        ? '/api/admin/acc-users' 
        : `/api/admin/acc-users?projectId=${projectFilter}`;
      const data = await apiRequest(url);
      return data.users || [];
    },
    // Auto-refresh every 30 seconds to ensure data sync
    refetchInterval: 30000,
    // Refresh when window regains focus
    refetchOnWindowFocus: true,
    // Refresh when network reconnects
    refetchOnReconnect: true,
  });

  // Sync ACC users mutation
  const syncUsersMutation = useMutation({
    mutationFn: async (projectId: string) => {
      const response = await apiRequest('/api/admin/acc-users/sync', {
        method: 'POST',
        body: JSON.stringify({ projectId })
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/acc-users'] });
      toast({
        title: "Sync Successful",
        description: "ACC user data has been successfully synchronized",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Sync Failed",
        description: error.message || "Error occurred while syncing ACC user data",
        variant: "destructive",
      });
    },
  });

  const [columns, setColumns] = useState<ColumnConfig[]>([
    { key: 'name', label: 'Name', visible: true, width: 'w-48', frozen: true },
    { key: 'email', label: 'Email', visible: true, width: 'w-64' },
    { key: 'companyNames', label: 'Company', visible: true, width: 'w-48' },
    { key: 'projectIds', label: 'Projects', visible: true, width: 'w-48' },
    { key: 'status', label: 'Status', visible: true, width: 'w-24' },
    { key: 'lastAccessedAt', label: 'Last Access', visible: true, width: 'w-40' },
    { key: 'createdAt', label: 'Created', visible: true, width: 'w-40' },
  ]);

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleSyncUsers = async () => {
    // Here we can show a project selection dialog or use default project
    // For now, use the first available project ID
    if (users.length > 0 && users[0].projectIds.length > 0) {
      await syncUsersMutation.mutateAsync(users[0].projectIds[0]);
    } else {
      toast({
        title: "Cannot Sync",
        description: "No available project ID for synchronization",
        variant: "destructive",
      });
    }
  };

  const toggleProjectExpansion = (userId: string) => {
    const newExpanded = new Set(expandedProjects);
    if (newExpanded.has(userId)) {
      newExpanded.delete(userId);
    } else {
      newExpanded.add(userId);
    }
    setExpandedProjects(newExpanded);
  };

  // Get visible columns
  const visibleColumns = columns.filter(col => col.visible);

  // Render cell content based on column key
  const renderCell = (user: ACCUser, columnKey: string) => {
    switch (columnKey) {
      case 'name':
        return (
          <div className="flex items-center space-x-3">
            <Avatar className="h-8 w-8">
              <AvatarImage src={user.profileImageUrl} />
              <AvatarFallback className="bg-blue-100 text-blue-600 text-xs">
                {getInitials(user.firstName, user.lastName)}
              </AvatarFallback>
            </Avatar>
            <div>
              <div className="font-medium">{user.firstName} {user.lastName}</div>
            </div>
          </div>
        );
      case 'email':
        return (
          <span 
            className="text-sm text-gray-600 truncate block" 
            title={user.email}
          >
            {user.email}
          </span>
        );
      case 'companyNames':
        return (
          <div className="space-y-1">
            {user.companyNames.length > 0 ? (
              user.companyNames.map((company, index) => (
                <Badge key={index} variant="outline" className="text-xs">
                  {company}
                </Badge>
              ))
            ) : (
              <span className="text-gray-400">N/A</span>
            )}
          </div>
        );
      case 'projectIds':
        if (user.projectNames.length === 0) {
          return <span className="text-gray-400">N/A</span>;
        }
        
        const isExpanded = expandedProjects.has(user.id);
        const maxDisplayProjects = 3;
        
        return (
          <div className="space-y-1">
            {isExpanded ? (
              // Show all projects when expanded
              <div className="space-y-1">
                {user.projectNames.map((projectName, index) => (
                  <Badge key={index} variant="secondary" className="text-xs">
                    {projectName}
                  </Badge>
                ))}
                {user.projectNames.length > maxDisplayProjects && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-xs text-blue-600 hover:text-blue-800"
                    onClick={() => toggleProjectExpansion(user.id)}
                  >
                    <ChevronUp className="w-3 h-3 mr-1" />
                    Show Less
                  </Button>
                )}
              </div>
            ) : (
              // Show collapsed view
              <div className="space-y-1">
                <div className="flex flex-wrap gap-1">
                  {user.projectNames.slice(0, maxDisplayProjects).map((projectName, index) => (
                    <Badge key={index} variant="secondary" className="text-xs">
                      {projectName}
                    </Badge>
                  ))}
                </div>
                {user.projectNames.length > maxDisplayProjects && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-xs text-blue-600 hover:text-blue-800"
                    onClick={() => toggleProjectExpansion(user.id)}
                  >
                    <ChevronDown className="w-3 h-3 mr-1" />
                    +{user.projectNames.length - maxDisplayProjects} more
                  </Button>
                )}
              </div>
            )}
          </div>
        );
      case 'status':
        return (
          <Badge variant={user.isActive ? "default" : "secondary"}>
            {user.isActive ? (
              <>
                <CheckCircle className="w-3 h-3 mr-1" />
                Active
              </>
            ) : (
              <>
                <XCircle className="w-3 h-3 mr-1" />
                Inactive
              </>
            )}
          </Badge>
        );
      case 'lastAccessedAt':
        return user.lastAccessedAt ? (
          <span className="text-sm text-gray-600">
            {new Date(user.lastAccessedAt).toLocaleDateString()}
          </span>
        ) : (
          <span className="text-gray-400">Never</span>
        );
      case 'createdAt':
        return (
          <span className="text-sm text-gray-600">
            {new Date(user.createdAt).toLocaleDateString()}
          </span>
        );
      default:
        return null;
    }
  };

  // Filter and sort users
  const filteredUsers = users.filter(user => {
    const matchesSearch = searchTerm === '' || 
      user.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || user.status === statusFilter;
    
    // Project filter is handled by the API query, so no additional filtering needed here
    return matchesSearch && matchesStatus;
  });

  // Sort users
  const sortedUsers = [...filteredUsers].sort((a, b) => {
    const aValue = a[sortField as keyof ACCUser];
    const bValue = b[sortField as keyof ACCUser];
    
    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Pagination
  const totalItems = sortedUsers.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedUsers = sortedUsers.slice(startIndex, startIndex + itemsPerPage);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Scroll handler for sticky columns
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollX > 0);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (error) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="p-6">
            <div className="text-center text-red-600">
              <p>Error loading ACC user data: {error.message}</p>
              <Button onClick={() => refetch()} className="mt-2">
                Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <Users className="w-8 h-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total ACC Users</p>
                <p className="text-2xl font-bold text-gray-900">{users.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <CheckCircle className="w-8 h-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Active Users</p>
                <p className="text-2xl font-bold text-gray-900">
                  {users.filter(user => user.isActive).length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <Building2 className="w-8 h-8 text-orange-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Companies</p>
                <p className="text-2xl font-bold text-gray-900">
                  {new Set(users.flatMap(user => user.companyNames || [])).size}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <Calendar className="w-8 h-8 text-purple-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Projects</p>
                <p className="text-2xl font-bold text-gray-900">
                  {new Set(users.flatMap(user => user.projectIds || [])).size}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      {showFilters && (
        <Card>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
              <Select value={projectFilter} onValueChange={setProjectFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by project" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Projects</SelectItem>
                  {projectsData?.projects?.map((project) => (
                    <SelectItem key={project.id} value={project.id}>
                      {project.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex items-center gap-2">
                <Button
                  onClick={handleSyncUsers}
                  disabled={syncUsersMutation.isPending}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {syncUsersMutation.isPending ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <RotateCcw className="w-4 h-4 mr-2" />
                  )}
                  Sync Users
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Table */}
      <Card className="overflow-hidden" style={{marginBottom: '8px'}}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-2xl font-bold text-gray-900">ACC Users Management</CardTitle>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2"
              >
                <Search className="w-4 h-4" />
                {showFilters ? 'Hide Filters' : 'Show Filters'}
              </Button>
              
              <ColumnConfigDropdown 
                columns={columns}
                onColumnsChange={setColumns}
              />
              
              <Button
                onClick={handleSyncUsers}
                disabled={syncUsersMutation.isPending}
                size="sm"
                className="bg-blue-600 hover:bg-blue-700"
              >
                {syncUsersMutation.isPending ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <RotateCcw className="w-4 h-4 mr-2" />
                )}
                Sync Users
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {visibleColumns.map((column) => (
                    <TableHead 
                      key={column.key}
                      className={`${column.width} py-3 cursor-pointer select-none${column.frozen ? ' sticky left-0 z-20 bg-white shadow-lg' : ''}`}
                      onClick={() => handleSort(column.key)}
                    >
                      <div className="flex items-center space-x-1">
                        <span>{column.label}</span>
                        {sortField === column.key && (
                          sortDirection === 'asc' ? 
                            <ChevronUp className="w-4 h-4" /> : 
                            <ChevronDown className="w-4 h-4" />
                        )}
                      </div>
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={visibleColumns.length} className="text-center py-8">
                      <div className="animate-pulse space-y-2">
                        <div className="h-4 bg-gray-200 rounded w-1/4 mx-auto"></div>
                        <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto"></div>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : paginatedUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={visibleColumns.length} className="text-center py-8">
                      <div className="text-gray-500">
                        <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                        <p className="text-lg font-medium">No ACC users found</p>
                        <p className="text-sm">Click "Sync Users" button to fetch user data from Autodesk ACC</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedUsers.map((user, index) => (
                    <TableRow key={`${user.id}-${index}`} className="hover:bg-gray-50">
                      {visibleColumns.map((column) => (
                        <TableCell 
                          key={column.key} 
                          className={`${column.width} py-2${column.frozen ? ' sticky left-0 z-20 bg-white shadow-lg hover:bg-white' : ''}${column.frozen && isScrolled ? ' border-r border-gray-300' : ''}`}
                        >
                          {renderCell(user, column.key)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white mt-4">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminACCUsers;

